import { cn } from '@/lib/utils'
import { generateRandomColor } from '@/utils'
import { useState, type ComponentProps } from 'react'
import { FAIcon } from './fa-icon'

function generateColors(num: number, include: string) {
  return [
    include,
    ...Array(num)
      .fill('')
      .map(_ => generateRandomColor()),
  ]
}

interface Props extends Omit<ComponentProps<'div'>, 'onChange'> {
  value: string

  onChange: (value: string) => void
}

export function SimpleColorPicker({
  className,

  value,

  onChange,

  ...props
}: Props) {
  const [colors, setColors] = useState(generateColors(7, value))

  function handleColorKeyDown(evt: React.KeyboardEvent<HTMLDivElement>) {
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
      const dir = evt.key === 'ArrowLeft' ? -1 : 1
      const currentIndex = colors.indexOf(value)
      const nextIndex = (currentIndex + dir + colors.length) % colors.length
      const nextColor = colors[nextIndex]
      if (nextColor) onChange(nextColor)
    }
  }

  function handleRefreshKeyDown(evt: React.KeyboardEvent<HTMLDivElement>) {
    if (evt.key === 'Enter' || evt.key === ' ') {
      setColors(generateColors(7, value))
    }
  }

  return (
    <div className={cn('flex gap-1', className)} {...props}>
      {colors.map((color, idx) => (
        <div
          key={color}
          className={cn(
            'size-7 rounded-sm cursor-pointer',
            'focus-visible:outline-secondary',
            value === color && 'ring-2'
          )}
          role="button"
          tabIndex={value === color ? 0 : -1}
          style={{
            backgroundColor: color,
          }}
          aria-label={`Select color ${idx + 1}`}
          onKeyDown={handleColorKeyDown}
          onClick={() => onChange(color)}
        />
      ))}

      <div
        className="flex items-center justify-center size-7 cursor-pointer hover:*:rotate-45"
        role="button"
        tabIndex={0}
        aria-label="Refresh colors"
        onKeyDown={handleRefreshKeyDown}
        onClick={() => setColors(generateColors(7, value))}
      >
        <FAIcon className="transition-transform" icon="refresh" />
      </div>
    </div>
  )
}
